import type { ImageProps } from 'next/image';
import Image from 'next/image';

import { cn } from '@/lib/cn';
import PigelloLogoNoText from '@/public/assets/logo/pigello_blue.svg';
import PigelloLogo from '@/public/assets/logo/pigello_blue_text.svg';
import {
  AirVentIcon,
  AlertTriangleIcon,
  AlignLeftIcon,
  AlignRightIcon,
  ArchiveIcon,
  ArchiveXIcon,
  ArrowDownIcon,
  ArrowDownWideNarrowIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowRightLeftIcon,
  ArrowUpIcon,
  ArrowUpNarrowWideIcon,
  AtSignIcon,
  BadgeCheckIcon,
  BanIcon,
  BanknoteIcon,
  BathIcon,
  BellIcon,
  BellOffIcon,
  BellRingIcon,
  BikeIcon,
  BoldIcon,
  BookmarkIcon,
  BookmarkPlusIcon,
  BoxIcon,
  BoxesIcon,
  BriefcaseBusinessIcon,
  BriefcaseIcon,
  BugIcon,
  Building2Icon,
  BuildingIcon,
  BusIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  CalendarIcon,
  CalendarPlusIcon,
  CarIcon,
  ChartColumnIcon,
  ChartPieIcon,
  CheckCheckIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ChevronsDownIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
  ChevronsUpDownIcon,
  ChevronsUpIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleIcon,
  CircleMinusIcon,
  CirclePauseIcon,
  CirclePlayIcon,
  CirclePlusIcon,
  CircleQuestionMarkIcon,
  CircleUserIcon,
  CircleXIcon,
  ClipboardCheckIcon,
  ClipboardIcon,
  ClipboardListIcon,
  ClockIcon,
  CloudDownloadIcon,
  CloudUploadIcon,
  CodeIcon,
  CoffeeIcon,
  CompassIcon,
  ConciergeBellIcon,
  CookingPotIcon,
  CopyIcon,
  CreditCardIcon,
  DatabaseIcon,
  DiscAlbumIcon,
  DoorClosedLockedIcon,
  DotIcon,
  DownloadIcon,
  DumbbellIcon,
  EllipsisIcon,
  EthernetPortIcon,
  EuroIcon,
  ExpandIcon,
  EyeIcon,
  EyeOffIcon,
  FanIcon,
  FenceIcon,
  FileChartColumnIcon,
  FileCheckIcon,
  FileIcon,
  FilePlusIcon,
  FileSearchIcon,
  FileTextIcon,
  FileUpIcon,
  FilesIcon,
  FlameIcon,
  FlaskConicalIcon,
  FolderClosedIcon,
  FolderOpenIcon,
  FolderPlusIcon,
  ForkliftIcon,
  FunnelIcon,
  GaugeIcon,
  GiftIcon,
  GlobeIcon,
  GripVerticalIcon,
  HandIcon,
  HandshakeIcon,
  HashIcon,
  HeartIcon,
  HeaterIcon,
  HistoryIcon,
  HouseIcon,
  HousePlusIcon,
  ImageIcon,
  InboxIcon,
  InfoIcon,
  ItalicIcon,
  KeyIcon,
  LandmarkIcon,
  LayersIcon,
  LayoutDashboardIcon,
  LayoutGridIcon,
  LifeBuoyIcon,
  LinkIcon,
  ListIcon,
  Loader2Icon,
  LockIcon,
  LockOpenIcon,
  LogInIcon,
  LogOutIcon,
  MailIcon,
  MailOpenIcon,
  MapIcon,
  MapPinIcon,
  MegaphoneIcon,
  MenuIcon,
  MessageCircleMoreIcon,
  MessageSquareIcon,
  MessagesSquareIcon,
  MilestoneIcon,
  MinimizeIcon,
  MinusIcon,
  MonitorIcon,
  MousePointerClickIcon,
  PaintRollerIcon,
  PaperclipIcon,
  ParkingCircleIcon,
  ParkingSquareIcon,
  PauseIcon,
  PencilIcon,
  PercentIcon,
  PersonStandingIcon,
  PhoneIcon,
  PieChartIcon,
  PlayIcon,
  PlugIcon,
  PlusIcon,
  PowerIcon,
  PresentationIcon,
  PrinterIcon,
  PuzzleIcon,
  QrCodeIcon,
  ReceiptEuroIcon,
  ReceiptTextIcon,
  Redo2Icon,
  RefreshCwIcon,
  RepeatIcon,
  RocketIcon,
  RotateCcwIcon,
  SaveIcon,
  SearchIcon,
  SendHorizontalIcon,
  SendIcon,
  SettingsIcon,
  ShareIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  ShieldMinusIcon,
  ShirtIcon,
  ShowerHeadIcon,
  SignpostIcon,
  SlidersHorizontalIcon,
  SmartphoneIcon,
  SparklesIcon,
  SpeechIcon,
  SquareArrowOutUpRightIcon,
  SquareMousePointerIcon,
  SquarePenIcon,
  SquarePlusIcon,
  StarIcon,
  StickyNoteIcon,
  StoreIcon,
  StrikethroughIcon,
  SunIcon,
  TagIcon,
  TerminalIcon,
  TextCursorInputIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
  ToiletIcon,
  TrainFrontIcon,
  Trash2Icon,
  TreePineIcon,
  TreesIcon,
  TrendingDownIcon,
  TrendingUpIcon,
  TruckIcon,
  TvIcon,
  UnderlineIcon,
  Undo2Icon,
  UnlinkIcon,
  UploadIcon,
  UserIcon,
  UserMinusIcon,
  UserPlusIcon,
  UserSearchIcon,
  UserX2Icon,
  UsersIcon,
  UtensilsIcon,
  VariableIcon,
  WarehouseIcon,
  WashingMachineIcon,
  WorkflowIcon,
  WrenchIcon,
  XIcon,
  ZapIcon,
  ZoomInIcon,
} from 'lucide-react';
import type { FC } from 'react';
import { StringIcon } from './icons/string-icon';

/**
 * Shared icons, so one concept keeps one glyph and can be swapped from a single
 * line. Add a key when the icon carries a concept that recurs — models, actions,
 * statuses, navigation. A one-off decorative icon does not need one; import it
 * at the call site instead.
 *
 * Keys are camelCase and name the concept, not the picture (`moreOptions`, not
 * `ellipsisVertical`). Use `edit`/`delete` on a control that performs the action,
 * `pencil`/`trash` when it does not. Model keys are the exception — see the block
 * below. Keys may share a component on purpose, to be split apart later.
 */
export const Icons = {
  // Brand and third-party logos

  apple: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' {...props}>
      <path
        d='M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701'
        fill='currentColor'
      />
    </svg>
  ),
  aria: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' fill='currentColor' {...props}>
      <path d='M13.966 22.624l-1.69-4.281H8.122l3.892-9.144 5.662 13.425zM8.884 1.376H0v21.248zm15.116 0h-8.884L24 22.624Z' />
    </svg>
  ),
  gitHub: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 438.549 438.549' {...props}>
      <path
        fill='currentColor'
        d='M409.132 114.573c-19.608-33.596-46.205-60.194-79.798-79.8-33.598-19.607-70.277-29.408-110.063-29.408-39.781 0-76.472 9.804-110.063 29.408-33.596 19.605-60.192 46.204-79.8 79.8C9.803 148.168 0 184.854 0 224.63c0 47.78 13.94 90.745 41.827 128.906 27.884 38.164 63.906 64.572 108.063 79.227 5.14.954 8.945.283 11.419-1.996 2.475-2.282 3.711-5.14 3.711-8.562 0-.571-.049-5.708-.144-15.417a2549.81 2549.81 0 01-.144-25.406l-6.567 1.136c-4.187.767-9.469 1.092-15.846 1-6.374-.089-12.991-.757-19.842-1.999-6.854-1.231-13.229-4.086-19.13-8.559-5.898-4.473-10.085-10.328-12.56-17.556l-2.855-6.57c-1.903-4.374-4.899-9.233-8.992-14.559-4.093-5.331-8.232-8.945-12.419-10.848l-1.999-1.431c-1.332-.951-2.568-2.098-3.711-3.429-1.142-1.331-1.997-2.663-2.568-3.997-.572-1.335-.098-2.43 1.427-3.289 1.525-.859 4.281-1.276 8.28-1.276l5.708.853c3.807.763 8.516 3.042 14.133 6.851 5.614 3.806 10.229 8.754 13.846 14.842 4.38 7.806 9.657 13.754 15.846 17.847 6.184 4.093 12.419 6.136 18.699 6.136 6.28 0 11.704-.476 16.274-1.423 4.565-.952 8.848-2.383 12.847-4.285 1.713-12.758 6.377-22.559 13.988-29.41-10.848-1.14-20.601-2.857-29.264-5.14-8.658-2.286-17.605-5.996-26.835-11.14-9.235-5.137-16.896-11.516-22.985-19.126-6.09-7.614-11.088-17.61-14.987-29.979-3.901-12.374-5.852-26.648-5.852-42.826 0-23.035 7.52-42.637 22.557-58.817-7.044-17.318-6.379-36.732 1.997-58.24 5.52-1.715 13.706-.428 24.554 3.853 10.85 4.283 18.794 7.952 23.84 10.994 5.046 3.041 9.089 5.618 12.135 7.708 17.705-4.947 35.976-7.421 54.818-7.421s37.117 2.474 54.823 7.421l10.849-6.849c7.419-4.57 16.18-8.758 26.262-12.565 10.088-3.805 17.802-4.853 23.134-3.138 8.562 21.509 9.325 40.922 2.279 58.24 15.036 16.18 22.559 35.787 22.559 58.817 0 16.178-1.958 30.497-5.853 42.966-3.9 12.471-8.941 22.457-15.125 29.979-6.191 7.521-13.901 13.85-23.131 18.986-9.232 5.14-18.182 8.85-26.84 11.136-8.662 2.286-18.415 4.004-29.263 5.146 9.894 8.562 14.842 22.077 14.842 40.539v60.237c0 3.422 1.19 6.279 3.572 8.562 2.379 2.279 6.136 2.95 11.276 1.995 44.163-14.653 80.185-41.062 108.068-79.226 27.88-38.161 41.825-81.126 41.825-128.906-.01-39.771-9.818-76.454-29.414-110.049z'
      ></path>
    </svg>
  ),
  google: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' {...props}>
      <path
        fill='currentColor'
        d='M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z'
      />
    </svg>
  ),
  logo: ({ width = 100, ...props }: Omit<ImageProps, 'src' | 'alt'>) => (
    <Image
      {...props}
      src={PigelloLogo}
      className={cn(
        'dark:[filter:brightness(2.3)_saturate(2.3)_grayscale(0.3)_hue-rotate(15deg)]',
        props.className
      )}
      alt='Pigello'
      width={width}
    />
  ),
  logoNoText: ({ width = 50, ...props }: Omit<ImageProps, 'src' | 'alt'>) => (
    <Image
      {...props}
      src={PigelloLogoNoText}
      className={cn(
        'dark:[filter:brightness(2.3)_saturate(2.3)_grayscale(0.3)_hue-rotate(15deg)]',
        props.className
      )}
      alt='Pigello'
      width={width}
    />
  ),
  npm: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M1.763 0C.786 0 0 .786 0 1.763v20.474C0 23.214.786 24 1.763 24h20.474c.977 0 1.763-.786 1.763-1.763V1.763C24 .786 23.214 0 22.237 0zM5.13 5.323l13.837.019-.009 13.836h-3.464l.01-10.382h-3.456L12.04 19.17H5.113z'
        fill='currentColor'
      />
    </svg>
  ),
  paypal: (props: React.SVGProps<SVGSVGElement>) => (
    <svg role='img' viewBox='0 0 24 24' {...props}>
      <path
        d='M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z'
        fill='currentColor'
      />
    </svg>
  ),
  pnpm: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M0 0v7.5h7.5V0zm8.25 0v7.5h7.498V0zm8.25 0v7.5H24V0zM8.25 8.25v7.5h7.498v-7.5zm8.25 0v7.5H24v-7.5zM0 16.5V24h7.5v-7.5zm8.25 0V24h7.498v-7.5zm8.25 0V24H24v-7.5z'
        fill='currentColor'
      />
    </svg>
  ),
  radix: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 25 25' fill='none' {...props}>
      <path
        d='M12 25C7.58173 25 4 21.4183 4 17C4 12.5817 7.58173 9 12 9V25Z'
        fill='currentcolor'
      ></path>
      <path d='M12 0H4V8H12V0Z' fill='currentcolor'></path>
      <path
        d='M17 8C19.2091 8 21 6.20914 21 4C21 1.79086 19.2091 0 17 0C14.7909 0 13 1.79086 13 4C13 6.20914 14.7909 8 17 8Z'
        fill='currentcolor'
      ></path>
    </svg>
  ),
  react: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M14.23 12.004a2.236 2.236 0 0 1-2.235 2.236 2.236 2.236 0 0 1-2.236-2.236 2.236 2.236 0 0 1 2.235-2.236 2.236 2.236 0 0 1 2.236 2.236zm2.648-10.69c-1.346 0-3.107.96-4.888 2.622-1.78-1.653-3.542-2.602-4.887-2.602-.41 0-.783.093-1.106.278-1.375.793-1.683 3.264-.973 6.365C1.98 8.917 0 10.42 0 12.004c0 1.59 1.99 3.097 5.043 4.03-.704 3.113-.39 5.588.988 6.38.32.187.69.275 1.102.275 1.345 0 3.107-.96 4.888-2.624 1.78 1.654 3.542 2.603 4.887 2.603.41 0 .783-.09 1.106-.275 1.374-.792 1.683-3.263.973-6.365C22.02 15.096 24 13.59 24 12.004c0-1.59-1.99-3.097-5.043-4.032.704-3.11.39-5.587-.988-6.38-.318-.184-.688-.277-1.092-.278zm-.005 1.09v.006c.225 0 .406.044.558.127.666.382.955 1.835.73 3.704-.054.46-.142.945-.25 1.44-.96-.236-2.006-.417-3.107-.534-.66-.905-1.345-1.727-2.035-2.447 1.592-1.48 3.087-2.292 4.105-2.295zm-9.77.02c1.012 0 2.514.808 4.11 2.28-.686.72-1.37 1.537-2.02 2.442-1.107.117-2.154.298-3.113.538-.112-.49-.195-.964-.254-1.42-.23-1.868.054-3.32.714-3.707.19-.09.4-.127.563-.132zm4.882 3.05c.455.468.91.992 1.36 1.564-.44-.02-.89-.034-1.345-.034-.46 0-.915.01-1.36.034.44-.572.895-1.096 1.345-1.565zM12 8.1c.74 0 1.477.034 2.202.093.406.582.802 1.203 1.183 1.86.372.64.71 1.29 1.018 1.946-.308.655-.646 1.31-1.013 1.95-.38.66-.773 1.288-1.18 1.87-.728.063-1.466.098-2.21.098-.74 0-1.477-.035-2.202-.093-.406-.582-.802-1.204-1.183-1.86-.372-.64-.71-1.29-1.018-1.946.303-.657.646-1.313 1.013-1.954.38-.66.773-1.286 1.18-1.868.728-.064 1.466-.098 2.21-.098zm-3.635.254c-.24.377-.48.763-.704 1.16-.225.39-.435.782-.635 1.174-.265-.656-.49-1.31-.676-1.947.64-.15 1.315-.283 2.015-.386zm7.26 0c.695.103 1.365.23 2.006.387-.18.632-.405 1.282-.66 1.933-.2-.39-.41-.783-.64-1.174-.225-.392-.465-.774-.705-1.146zm3.063.675c.484.15.944.317 1.375.498 1.732.74 2.852 1.708 2.852 2.476-.005.768-1.125 1.74-2.857 2.475-.42.18-.88.342-1.355.493-.28-.958-.646-1.956-1.1-2.98.45-1.017.81-2.01 1.085-2.964zm-13.395.004c.278.96.645 1.957 1.1 2.98-.45 1.017-.812 2.01-1.086 2.964-.484-.15-.944-.318-1.37-.5-1.732-.737-2.852-1.706-2.852-2.474 0-.768 1.12-1.742 2.852-2.476.42-.18.88-.342 1.356-.494zm11.678 4.28c.265.657.49 1.312.676 1.948-.64.157-1.316.29-2.016.39.24-.375.48-.762.705-1.158.225-.39.435-.788.636-1.18zm-9.945.02c.2.392.41.783.64 1.175.23.39.465.772.705 1.143-.695-.102-1.365-.23-2.006-.386.18-.63.406-1.282.66-1.933zM17.92 16.32c.112.493.2.968.254 1.423.23 1.868-.054 3.32-.714 3.708-.147.09-.338.128-.563.128-1.012 0-2.514-.807-4.11-2.28.686-.72 1.37-1.536 2.02-2.44 1.107-.118 2.154-.3 3.113-.54zm-11.83.01c.96.234 2.006.415 3.107.532.66.905 1.345 1.727 2.035 2.446-1.595 1.483-3.092 2.295-4.11 2.295-.22-.005-.406-.05-.553-.132-.666-.38-.955-1.834-.73-3.703.054-.46.142-.944.25-1.438zm4.56.64c.44.02.89.034 1.345.034.46 0 .915-.01 1.36-.034-.44.572-.895 1.095-1.345 1.565-.455-.47-.91-.993-1.36-1.565z'
        fill='currentColor'
      />
    </svg>
  ),
  tailwind: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M12.001,4.8c-3.2,0-5.2,1.6-6,4.8c1.2-1.6,2.6-2.2,4.2-1.8c0.913,0.228,1.565,0.89,2.288,1.624 C13.666,10.618,15.027,12,18.001,12c3.2,0,5.2-1.6,6-4.8c-1.2,1.6-2.6,2.2-4.2,1.8c-0.913-0.228-1.565-0.89-2.288-1.624 C16.337,6.182,14.976,4.8,12.001,4.8z M6.001,12c-3.2,0-5.2,1.6-6,4.8c1.2-1.6,2.6-2.2,4.2-1.8c0.913,0.228,1.565,0.89,2.288,1.624 c1.177,1.194,2.538,2.576,5.512,2.576c3.2,0,5.2-1.6,6-4.8c-1.2,1.6-2.6,2.2-4.2,1.8c-0.913-0.228-1.565-0.89-2.288-1.624 C10.337,13.382,8.976,12,6.001,12z'
        fill='currentColor'
      />
    </svg>
  ),
  yarn: (props: React.SVGProps<SVGSVGElement>) => (
    <svg viewBox='0 0 24 24' {...props}>
      <path
        d='M12 0C5.375 0 0 5.375 0 12s5.375 12 12 12 12-5.375 12-12S18.625 0 12 0zm.768 4.105c.183 0 .363.053.525.157.125.083.287.185.755 1.154.31-.088.468-.042.551-.019.204.056.366.19.463.375.477.917.542 2.553.334 3.605-.241 1.232-.755 2.029-1.131 2.576.324.329.778.899 1.117 1.825.278.774.31 1.478.273 2.015a5.51 5.51 0 0 0 .602-.329c.593-.366 1.487-.917 2.553-.931.714-.009 1.269.445 1.353 1.103a1.23 1.23 0 0 1-.945 1.362c-.649.158-.95.278-1.821.843-1.232.797-2.539 1.242-3.012 1.39a1.686 1.686 0 0 1-.704.343c-.737.181-3.266.315-3.466.315h-.046c-.783 0-1.214-.241-1.45-.491-.658.329-1.51.19-2.122-.134a1.078 1.078 0 0 1-.58-1.153 1.243 1.243 0 0 1-.153-.195c-.162-.25-.528-.936-.454-1.946.056-.723.556-1.367.88-1.71a5.522 5.522 0 0 1 .408-2.256c.306-.727.885-1.348 1.32-1.737-.32-.537-.644-1.367-.329-2.21.227-.602.412-.936.82-1.08h-.005c.199-.074.389-.153.486-.259a3.418 3.418 0 0 1 2.298-1.103c.037-.093.079-.185.125-.283.31-.658.639-1.029 1.024-1.168a.94.94 0 0 1 .328-.06zm.006.7c-.507.016-1.001 1.519-1.001 1.519s-1.27-.204-2.266.871c-.199.218-.468.334-.746.44-.079.028-.176.023-.417.672-.371.991.625 2.094.625 2.094s-1.186.839-1.626 1.881c-.486 1.144-.338 2.261-.338 2.261s-.843.732-.899 1.487c-.051.663.139 1.2.343 1.515.227.343.51.176.51.176s-.561.653-.037.931c.477.25 1.283.394 1.71-.037.31-.31.371-1.001.486-1.283.028-.065.12.111.209.199.097.093.264.195.264.195s-.755.324-.445 1.066c.102.246.468.403 1.066.398.222-.005 2.664-.139 3.313-.296.375-.088.505-.283.505-.283s1.566-.431 2.998-1.357c.917-.598 1.293-.76 2.034-.936.612-.148.57-1.098-.241-1.084-.839.009-1.575.44-2.196.825-1.163.718-1.742.672-1.742.672l-.018-.032c-.079-.13.371-1.293-.134-2.678-.547-1.515-1.413-1.881-1.344-1.997.297-.5 1.038-1.297 1.334-2.78.176-.899.13-2.377-.269-3.151-.074-.144-.732.241-.732.241s-.616-1.371-.788-1.483a.271.271 0 0 0-.157-.046z'
        fill='currentColor'
      />
    </svg>
  ),
  // File types

  doc: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM16.8849 48.2H20.4943C21.5284 48.2 22.4167 47.9961 23.1591 47.5884C23.9048 47.1808 24.4766 46.5958 24.8743 45.8335C25.2753 45.0712 25.4759 44.1597 25.4759 43.0991C25.4759 42.0418 25.2753 41.1337 24.8743 40.3747C24.4766 39.6157 23.9081 39.034 23.169 38.6296C22.4299 38.222 21.5483 38.0181 20.5241 38.0181H16.8849V48.2ZM20.4048 46.3555H19.0376V39.8626H20.4098C21.0462 39.8626 21.5814 39.9753 22.0156 40.2007C22.4498 40.426 22.7763 40.7774 22.995 41.2546C23.2171 41.7319 23.3281 42.3467 23.3281 43.0991C23.3281 43.8581 23.2171 44.4779 22.995 44.9585C22.7763 45.4357 22.4482 45.7887 22.0107 46.0174C21.5765 46.2428 21.0412 46.3555 20.4048 46.3555ZM35.7956 45.9428C36.2166 45.164 36.427 44.2194 36.427 43.109C36.427 41.9987 36.2166 41.0541 35.7956 40.2752C35.378 39.4964 34.8079 38.9031 34.0854 38.4954C33.3662 38.0844 32.5575 37.8789 31.6593 37.8789C30.7544 37.8789 29.9424 38.0844 29.2232 38.4954C28.504 38.9031 27.9355 39.4964 27.5179 40.2752C27.1003 41.0541 26.8915 41.9987 26.8915 43.109C26.8915 44.216 27.1003 45.159 27.5179 45.9379C27.9355 46.7168 28.504 47.3117 29.2232 47.7227C29.9424 48.1337 30.7544 48.3392 31.6593 48.3392C32.5575 48.3392 33.3662 48.1353 34.0854 47.7276C34.8079 47.3167 35.378 46.7217 35.7956 45.9428ZM33.9213 41.2894C34.1368 41.7833 34.2445 42.3898 34.2445 43.109C34.2445 43.8283 34.1368 44.4348 33.9213 44.9286C33.7092 45.4225 33.4093 45.797 33.0215 46.0522C32.6337 46.3074 32.1796 46.435 31.6593 46.435C31.1389 46.435 30.6848 46.3074 30.2971 46.0522C29.9093 45.797 29.6077 45.4225 29.3922 44.9286C29.1801 44.4348 29.074 43.8283 29.074 43.109C29.074 42.3898 29.1801 41.7833 29.3922 41.2894C29.6077 40.7956 29.9093 40.4211 30.2971 40.1659C30.6848 39.9107 31.1389 39.783 31.6593 39.783C32.1796 39.783 32.6337 39.9107 33.0215 40.1659C33.4093 40.4211 33.7092 40.7956 33.9213 41.2894ZM44.8278 41.5828H47.0053C46.9357 40.9928 46.7766 40.4691 46.5281 40.0117C46.2828 39.5544 45.9646 39.1682 45.5735 38.8534C45.1824 38.5352 44.7333 38.2932 44.2262 38.1275C43.7224 37.9618 43.1755 37.8789 42.5856 37.8789C41.6907 37.8789 40.8853 38.0844 40.1694 38.4954C39.4535 38.9031 38.8867 39.4964 38.4691 40.2752C38.0515 41.0541 37.8427 41.9987 37.8427 43.109C37.8427 44.216 38.0482 45.159 38.4592 45.9379C38.8735 46.7168 39.4369 47.3117 40.1495 47.7227C40.8654 48.1337 41.6774 48.3392 42.5856 48.3392C43.2385 48.3392 43.8235 48.2414 44.3406 48.0458C44.8609 47.847 45.3084 47.5785 45.6829 47.2404C46.0607 46.8991 46.3607 46.5129 46.5827 46.0821C46.8081 45.6479 46.949 45.1971 47.0053 44.7298L44.8278 44.7198C44.7781 44.9916 44.6886 45.2336 44.5593 45.4457C44.4334 45.6578 44.2743 45.8384 44.082 45.9876C43.8898 46.1334 43.6694 46.2445 43.4208 46.3207C43.1755 46.3969 42.9087 46.435 42.6204 46.435C42.1067 46.435 41.6542 46.3091 41.2631 46.0572C40.872 45.8053 40.5671 45.4324 40.3484 44.9386C40.1329 44.4447 40.0252 43.8349 40.0252 43.109C40.0252 42.4031 40.1329 41.8032 40.3484 41.3093C40.5638 40.8122 40.8671 40.4343 41.2582 40.1758C41.6493 39.914 42.1083 39.783 42.6353 39.783C42.927 39.783 43.1971 39.8245 43.4457 39.9073C43.6976 39.9902 43.9196 40.1095 44.1119 40.2653C44.3041 40.4211 44.4615 40.61 44.5842 40.8321C44.7068 41.0508 44.788 41.301 44.8278 41.5828Z'
        fill='currentColor'
      />
    </svg>
  ),
  excel: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM23.0983 41.2965L20.9864 37.7272H18.4756L21.692 42.9636L18.4142 48.2H20.9506L23.0983 44.6255H23.1801L25.3278 48.2H27.8744L24.6068 42.9636L27.8028 37.7272H25.3023L23.1801 41.2965H23.0983ZM29.204 37.7272V48.2H35.908V46.3744H31.4182V37.7272H29.204ZM42.5288 39.7778C42.8391 40.0062 43.0146 40.3267 43.0555 40.7392H45.1777C45.1675 40.1153 45.0004 39.5664 44.6766 39.0926C44.3527 38.6187 43.8993 38.2488 43.3163 37.9829C42.7368 37.717 42.0584 37.584 41.2811 37.584C40.5175 37.584 39.8357 37.717 39.2357 37.9829C38.6357 38.2488 38.1635 38.6187 37.8192 39.0926C37.4783 39.5664 37.3095 40.1204 37.3129 40.7545C37.3095 41.5284 37.5635 42.1437 38.0749 42.6005C38.5862 43.0573 39.2834 43.3931 40.1663 43.6079L41.3067 43.8943C41.6885 43.9863 42.0107 44.0886 42.2732 44.2011C42.5391 44.3136 42.7402 44.45 42.8766 44.6102C43.0163 44.7704 43.0862 44.9681 43.0862 45.2034C43.0862 45.4556 43.0095 45.6789 42.8561 45.8732C42.7027 46.0676 42.4862 46.2193 42.2067 46.3284C41.9305 46.4375 41.605 46.492 41.23 46.492C40.8482 46.492 40.5055 46.434 40.2021 46.3181C39.9021 46.1988 39.6618 46.0232 39.4811 45.7914C39.3038 45.5562 39.2033 45.263 39.1794 44.9119H37.0368C37.0538 45.6653 37.2362 46.2977 37.584 46.809C37.9351 47.317 38.4226 47.7005 39.0464 47.9596C39.6737 48.2187 40.4101 48.3482 41.2555 48.3482C42.1078 48.3482 42.8357 48.2204 43.4391 47.9647C44.0459 47.7056 44.5095 47.3426 44.83 46.8755C45.1538 46.4051 45.3175 45.8494 45.3209 45.2085C45.3175 44.7721 45.2374 44.3852 45.0805 44.0477C44.9271 43.7102 44.7107 43.417 44.4311 43.1681C44.1516 42.9193 43.8209 42.7096 43.4391 42.5392C43.0572 42.3687 42.6379 42.2323 42.1811 42.1301L41.2402 41.9051C41.0118 41.8539 40.797 41.7926 40.5959 41.721C40.3947 41.646 40.2175 41.559 40.0641 41.4602C39.9107 41.3579 39.7896 41.2369 39.701 41.0971C39.6158 40.9573 39.5766 40.7937 39.5834 40.6062C39.5834 40.3812 39.6482 40.1801 39.7777 40.0028C39.9107 39.8255 40.1016 39.6875 40.3504 39.5886C40.5993 39.4863 40.9044 39.4352 41.2658 39.4352C41.7976 39.4352 42.2186 39.5494 42.5288 39.7778Z'
        fill='currentColor'
      />
    </svg>
  ),
  pdf: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM18.5102 37.7272V48.2H20.7244V44.8045H22.5858C23.3903 44.8045 24.0755 44.6562 24.6415 44.3596C25.2108 44.063 25.6454 43.6488 25.9454 43.117C26.2454 42.5852 26.3954 41.9715 26.3954 41.2761C26.3954 40.5806 26.2471 39.967 25.9505 39.4352C25.6574 38.9 25.2312 38.4823 24.6721 38.1823C24.113 37.8789 23.4363 37.7272 22.642 37.7272H18.5102ZM20.7244 43.0301H22.2278C22.654 43.0301 23.0051 42.9568 23.2812 42.8102C23.5608 42.6602 23.7687 42.4539 23.9051 42.1914C24.0449 41.9255 24.1147 41.6204 24.1147 41.2761C24.1147 40.9284 24.0449 40.625 23.9051 40.3659C23.7687 40.1034 23.5608 39.9005 23.2812 39.7573C23.0017 39.6107 22.6471 39.5375 22.2176 39.5375H20.7244V43.0301ZM27.8336 48.2H31.5461C32.6098 48.2 33.5234 47.9903 34.2871 47.571C35.0541 47.1517 35.6422 46.5499 36.0513 45.7659C36.4638 44.9818 36.67 44.0443 36.67 42.9534C36.67 41.8659 36.4638 40.9318 36.0513 40.1511C35.6422 39.3704 35.0575 38.7721 34.2973 38.3562C33.5371 37.9369 32.6302 37.7272 31.5768 37.7272H27.8336V48.2ZM31.4541 46.3028H30.0478V39.6244H31.4592C32.1138 39.6244 32.6643 39.7403 33.1109 39.9721C33.5575 40.2039 33.8933 40.5653 34.1183 41.0562C34.3467 41.5471 34.4609 42.1795 34.4609 42.9534C34.4609 43.734 34.3467 44.3715 34.1183 44.8659C33.8933 45.3568 33.5558 45.7198 33.1058 45.9551C32.6592 46.1869 32.1086 46.3028 31.4541 46.3028ZM38.3102 37.7272V48.2H40.5244V43.8738H44.7841V42.0482H40.5244V39.5528H45.2443V37.7272H38.3102Z'
        fill='currentColor'
      />
    </svg>
  ),
  stl: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM24.8963 39.8118C25.1979 40.0339 25.3686 40.3454 25.4084 40.7464H27.4716C27.4616 40.1399 27.2992 39.6063 26.9844 39.1456C26.6695 38.6849 26.2287 38.3253 25.6619 38.0668C25.0985 37.8082 24.4389 37.679 23.6832 37.679C22.9408 37.679 22.2779 37.8082 21.6946 38.0668C21.1113 38.3253 20.6522 38.6849 20.3175 39.1456C19.986 39.6063 19.822 40.1449 19.8253 40.7614C19.822 41.5137 20.0689 42.112 20.5661 42.5561C21.0632 43.0002 21.741 43.3267 22.5994 43.5355L23.7081 43.8139C24.0793 43.9034 24.3925 44.0028 24.6477 44.1122C24.9062 44.2216 25.1018 44.3542 25.2344 44.5099C25.3703 44.6657 25.4382 44.858 25.4382 45.0866C25.4382 45.3319 25.3636 45.549 25.2145 45.7379C25.0653 45.9268 24.8549 46.0743 24.5831 46.1804C24.3146 46.2865 23.9981 46.3395 23.6335 46.3395C23.2623 46.3395 22.9292 46.2831 22.6342 46.1705C22.3426 46.0545 22.1089 45.8838 21.9332 45.6584C21.7609 45.4297 21.6631 45.1446 21.6399 44.8033H19.5568C19.5734 45.5357 19.7507 46.1506 20.0888 46.6477C20.4302 47.1416 20.9041 47.5144 21.5107 47.7663C22.1205 48.0182 22.8364 48.1442 23.6584 48.1442C24.487 48.1442 25.1946 48.0199 25.7812 47.7713C26.3712 47.5194 26.822 47.1664 27.1335 46.7124C27.4484 46.255 27.6075 45.7147 27.6108 45.0916C27.6075 44.6674 27.5296 44.2912 27.3771 43.9631C27.228 43.6349 27.0175 43.3499 26.7457 43.108C26.474 42.866 26.1525 42.6622 25.7812 42.4964C25.41 42.3307 25.0024 42.1982 24.5582 42.0987L23.6435 41.88C23.4214 41.8303 23.2126 41.7706 23.017 41.701C22.8215 41.6281 22.6491 41.5436 22.5 41.4474C22.3509 41.348 22.2332 41.2304 22.147 41.0945C22.0642 40.9586 22.026 40.7995 22.0327 40.6172C22.0327 40.3984 22.0956 40.2029 22.2216 40.0305C22.3509 39.8582 22.5365 39.724 22.7784 39.6278C23.0204 39.5284 23.317 39.4787 23.6683 39.4787C24.1854 39.4787 24.5947 39.5897 24.8963 39.8118ZM28.671 37.8182V39.593H31.7882V48H33.916V39.593H37.0332V37.8182H28.671ZM38.4103 37.8182V48H44.9281V46.2251H40.563V37.8182H38.4103Z'
        fill='currentColor'
      />
    </svg>
  ),
  txt: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM18.4972 38.0181V39.793H21.6143V48.2H23.7422V39.793H26.8594V38.0181H18.4972ZM32.3082 41.4883L30.255 38.0181H27.8139L30.9411 43.109L27.7543 48.2H30.2202L32.3082 44.7248H32.3878L34.4759 48.2H36.9517L33.7749 43.109L36.8821 38.0181H34.451L32.3878 41.4883H32.3082ZM37.8565 38.0181V39.793H40.9737V48.2H43.1016V39.793H46.2188V38.0181H37.8565Z'
        fill='currentColor'
      />
    </svg>
  ),
  zip: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='64'
      height='64'
      viewBox='0 0 64 64'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M18.2631 7.41169C23.9765 7.4115 32.6146 7.41132 35.499 7.41182V17.4942C35.499 21.3234 38.6032 24.4275 42.4323 24.4275H51.6035V51.42C51.6035 54.3655 49.2157 56.7429 46.2702 56.7429H18.2656C15.3201 56.7429 12.9323 54.355 12.9323 51.4095V12.745C12.9323 9.79957 15.3176 7.41179 18.2631 7.41169ZM38.699 8.58083C42.7101 11.1514 48.7217 17.3729 50.8354 21.2275H42.4323C40.3705 21.2275 38.699 19.5561 38.699 17.4942V8.58083ZM21.7977 46.8857V48.2H29.8159V46.3744H24.5744L29.8057 39.0414V37.7272H21.7875V39.5528H27.0239L21.7977 46.8857ZM33.7291 48.2V37.7272H31.5149V48.2H33.7291ZM35.5509 37.7272V48.2H37.7651V44.8045H39.6264C40.431 44.8045 41.1162 44.6562 41.6821 44.3596C42.2514 44.063 42.6861 43.6488 42.9861 43.117C43.2861 42.5852 43.4361 41.9715 43.4361 41.2761C43.4361 40.5806 43.2878 39.967 42.9912 39.4352C42.698 38.9 42.2719 38.4823 41.7128 38.1823C41.1537 37.8789 40.477 37.7272 39.6827 37.7272H35.5509ZM37.7651 43.0301H39.2685C39.6946 43.0301 40.0457 42.9568 40.3219 42.8102C40.6014 42.6602 40.8094 42.4539 40.9457 42.1914C41.0855 41.9255 41.1554 41.6204 41.1554 41.2761C41.1554 40.9284 41.0855 40.625 40.9457 40.3659C40.8094 40.1034 40.6014 39.9005 40.3219 39.7573C40.0423 39.6107 39.6878 39.5375 39.2582 39.5375H37.7651V43.0301Z'
        fill='currentColor'
      />
    </svg>
  ),

  // Model keys — these are Matrix `modelName` values and are resolved DYNAMICALLY
  // by `getIcon()` below and `Icons[model]` in lib/roundings/connected-object-modal.tsx.
  // Nothing type-checks that lookup, so renaming one here silently renders no icon
  // in production. Never rename or remove a key in this block. A few of them
  // (`file`, `note`, `tag`, `notification`, `address`, `component`) double as
  // everyday UI concepts — that is fine to use, it just makes the rule matter more.
  address: MapPinIcon,
  apartment: BuildingIcon,
  apartmentcontract: FileTextIcon,
  article: FileCheckIcon,
  automaticdistributionquota: CalculatorIcon,
  blockcontract: FileTextIcon,
  brfapartment: BuildingIcon,
  brfcompany: LandmarkIcon,
  building: Building2Icon,
  commonarea: LayoutDashboardIcon,
  company: LayersIcon,
  component: BoxIcon,
  externalclient: BriefcaseIcon,
  externalsupplier: HandshakeIcon,
  file: FileIcon,
  industrialpremises: WarehouseIcon,
  industrialpremisescontract: FileTextIcon,
  invoice: FileTextIcon,
  invoicepayment: ReceiptEuroIcon,
  measurementseries: ChartColumnIcon,
  monitoringconfiguration: InboxIcon,
  note: StickyNoteIcon,
  notification: BellIcon,
  organizationuser: UserIcon,
  othercontract: FilesIcon,
  outdoorarea: TreesIcon,
  outdoorsection: TreePineIcon,
  outdoorsectioncontract: FileTextIcon,
  parkinglot: ParkingSquareIcon,
  parkingspot: CarIcon,
  parkingspotcontract: FileTextIcon,
  realestate: Building2Icon,
  region: MapIcon,
  roundingoccurrence: ClipboardCheckIcon,
  roundingsetting: RepeatIcon,
  segment: ChartPieIcon,
  sendout: SendHorizontalIcon,
  sensor: GaugeIcon,
  signabledocument: FileTextIcon,
  tag: TagIcon,
  tenant: UserIcon,
  verification: MonitorIcon,
  verificationgroup: MonitorIcon,
  workengagement: BriefcaseBusinessIcon,
  // Domain concepts that are not model names.

  administration: ClipboardCheckIcon,
  checkpoint: ClipboardCheckIcon,
  communication: MessagesSquareIcon,
  communities: MapIcon,
  contract: FileTextIcon,
  corporate: BriefcaseBusinessIcon,
  dashboard: BriefcaseBusinessIcon,
  errand: WrenchIcon,
  faultPlus: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      {...props}
      viewBox='0 0 18 22'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <path
        d='M8.35 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V3.00001C1 2.46957 1.21071 1.96087 1.58579 1.58579C1.96086 1.21072 2.46957 1.00001 3 1.00001H15C15.5304 1 16.0391 1.21071 16.4142 1.58579C16.7893 1.96086 17 2.46957 17 3V12.35'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M11 18H17'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M14 15V21'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M9 5.5V11'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
      />
      <path
        d='M9 14.01V14'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
      />
    </svg>
  ),
  fee: EuroIcon,
  imd: TrendingUpIcon,
  indexation: BanknoteIcon,
  inspections: FileCheckIcon,
  integration: PuzzleIcon,
  invoiceBasis: FileCheckIcon,
  listing: MegaphoneIcon,
  listingDetails: StoreIcon,
  market: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      fill='currentColor'
      role='img'
      width='20'
      height='20'
      viewBox='0 0 20 20'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path d='M2 3a1 1 0 0 1 2 0h13a1 1 0 1 1 0 2H4v12.5a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5V3Zm3 3.5c0-.28.22-.5.5-.5h11c.28 0 .5.22.5.5v7a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 5 13.5v-7Zm3 7c0 .28.22.5.5.5h1a.5.5 0 0 0 .5-.5v-1c0-.28.22-.5.5-.5h1c.28 0 .5.22.5.5v1c0 .28.22.5.5.5h1a.5.5 0 0 0 .5-.5v-2.55a1 1 0 0 0-.34-.75l-2.33-2.07a.5.5 0 0 0-.66 0L8.34 10.2a1 1 0 0 0-.34.75v2.55Z'></path>
    </svg>
  ),
  marketPosting: MilestoneIcon,
  preset: LayersIcon,
  releaseNotes: RocketIcon,
  renovation: PaintRollerIcon,
  subTenant: UserIcon,
  technicalmanagement: WrenchIcon,
  tenantApplicant: UserSearchIcon,
  tenants: UsersIcon,
  workflow: WorkflowIcon,

  // Navigation. Chevrons are named for what they mean, not which way they point;
  // several share a glyph on purpose so they can be split apart later.
  arrowDown: ArrowDownIcon,
  arrowLeft: ArrowLeftIcon,
  arrowRight: ArrowRightIcon,
  arrowUp: ArrowUpIcon,
  collapse: ChevronUpIcon,
  collapseAll: ChevronsUpIcon,
  collapseView: MinimizeIcon,
  drillIn: ChevronRightIcon,
  dropdown: ChevronDownIcon,
  expand: ChevronDownIcon,
  expandAll: ChevronsDownIcon,
  expandView: ExpandIcon,
  external: SquareArrowOutUpRightIcon,
  first: ChevronsLeftIcon,
  last: ChevronsRightIcon,
  menu: MenuIcon,
  menubar: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4.5 4.5C2.84315 4.5 1.5 5.84315 1.5 7.5V16.5C1.5 18.1569 2.84315 19.5 4.5 19.5H19.5C21.1569 19.5 22.5 18.1569 22.5 16.5V7.5C22.5 5.84315 21.1569 4.5 19.5 4.5H4.5ZM3.5 7.5C3.5 6.94772 3.94772 6.5 4.5 6.5H7V17.5H4.5C3.94772 17.5 3.5 17.0523 3.5 16.5V7.5ZM9 17.5H19.5C20.0523 17.5 20.5 17.0523 20.5 16.5V7.5C20.5 6.94772 20.0523 6.5 19.5 6.5H9V17.5Z'
        fill='currentColor'
      />
    </svg>
  ),
  moreOptions: EllipsisIcon,
  moveDown: ChevronDownIcon,
  moveUp: ChevronUpIcon,
  next: ChevronRightIcon,
  previous: ChevronLeftIcon,
  scrollDown: ChevronDownIcon,
  scrollUp: ChevronUpIcon,
  selectIndicator: ChevronsUpDownIcon,
  separator: ChevronRightIcon,

  // Status and feedback
  accepted: CircleUserIcon,
  active: CheckIcon,
  alert: CircleAlertIcon,
  blocked: BanIcon,
  crash: FlameIcon,
  dot: DotIcon,
  error: CircleXIcon,
  favorite: StarIcon,
  help: CircleQuestionMarkIcon,
  inactive: PowerIcon,
  info: InfoIcon,
  inProgress: FlaskConicalIcon,
  loader: Loader2Icon,
  noPermissions: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      width='96'
      height='96'
      viewBox='0 0 96 96'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path
        d='M48 12.5C38.9429 21.1009 26.8811 24.1743 14.392 24C12.8034 28.8399 11.9959 33.902 12 38.996C12 61.364 27.296 81.668 48 87C68.704 81.672 84 61.368 84 39C84 33.76 83.16 28.716 81.608 23.996H81C68.216 23.996 56.6 20.648 48 12.5Z'
        stroke='url(#paint0_linear_4788_14756)'
        strokeWidth='6'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <g opacity='0.9'>
        <path
          d='M39 40L57 58M57 40L39 58'
          stroke='black'
          strokeWidth='6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
        <path
          d='M39 40L57 58M57 40L39 58'
          stroke='url(#paint1_linear_4788_14756)'
          strokeWidth='6'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </g>
      <defs>
        <linearGradient
          id='paint0_linear_4788_14756'
          x1='21.5'
          y1='17'
          x2='53.5'
          y2='83.5'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#444444' />
          <stop offset='0.665' stopColor='#444444' />
          <stop offset='0.75964' stopColor='#777777' />
          <stop offset='1' />
        </linearGradient>
        <linearGradient
          id='paint1_linear_4788_14756'
          x1='41.375'
          y1='41.0872'
          x2='48.941'
          y2='57.3562'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor='#444444' />
          <stop offset='0.665' stopColor='#444444' />
          <stop offset='0.75964' stopColor='#777777' />
          <stop offset='1' />
        </linearGradient>
      </defs>
    </svg>
  ),
  paused: CirclePauseIcon,
  pending: CircleDashedIcon,
  securityWarning: ShieldAlertIcon,
  shieldCheck: ShieldCheckIcon,
  shieldMinus: ShieldMinusIcon,
  started: CirclePlayIcon,
  success: CircleCheckIcon,
  synced: CheckCheckIcon,
  thumbsDown: ThumbsDownIcon,
  thumbsUp: ThumbsUpIcon,
  verified: BadgeCheckIcon,
  warning: AlertTriangleIcon,

  // Actions
  add: PlusIcon,
  addCircle: CirclePlusIcon,
  addDate: CalendarPlusIcon,
  addFile: FilePlusIcon,
  addFolder: FolderPlusIcon,
  addUser: UserPlusIcon,
  archive: ArchiveIcon,
  archiveXMark: ArchiveXIcon,
  attachment: PaperclipIcon,
  bookmarkAdd: BookmarkPlusIcon,
  click: MousePointerClickIcon,
  cloudDownload: CloudDownloadIcon,
  cloudUpload: CloudUploadIcon,
  copy: CopyIcon,
  createNew: SquarePlusIcon,
  deactivateUser: UserMinusIcon,
  decline: HandIcon,
  delete: Trash2Icon,
  download: DownloadIcon,
  drag: GripVerticalIcon,
  edit: PencilIcon,
  explore: CompassIcon,
  filter: FunnelIcon,
  locked: LockIcon,
  login: LogInIcon,
  logout: LogOutIcon,
  noUser: UserX2Icon,
  openFolder: FolderOpenIcon,
  pause: PauseIcon,
  play: PlayIcon,
  preview: ZoomInIcon,
  print: PrinterIcon,
  redo: Redo2Icon,
  refresh: RefreshCwIcon,
  removeCircle: CircleMinusIcon,
  repeat: RepeatIcon,
  revert: RotateCcwIcon,
  save: SaveIcon,
  search: SearchIcon,
  searchDocument: FileSearchIcon,
  send: SendIcon,
  share: ShareIcon,
  sortAsc: ArrowUpNarrowWideIcon,
  sortDesc: ArrowDownWideNarrowIcon,
  transfer: ArrowRightLeftIcon,
  undo: Undo2Icon,
  unlink: UnlinkIcon,
  unlocked: LockOpenIcon,
  upload: UploadIcon,
  uploadDocument: FileUpIcon,

  // Objects and content
  ai: SparklesIcon,
  atSign: AtSignIcon,
  attribute: ZapIcon,
  billing: CreditCardIcon,
  bookmark: BookmarkIcon,
  bug: BugIcon,
  calculator: CalculatorIcon,
  calendar: CalendarIcon,
  category: LayersIcon,
  chart: ChartColumnIcon,
  check: CheckIcon,
  checklist: ClipboardListIcon,
  circle: CircleIcon,
  clipboard: ClipboardIcon,
  close: XIcon,
  code: CodeIcon,
  comment: MessageCircleMoreIcon,
  components: BoxesIcon,
  database: DatabaseIcon,
  date: CalendarIcon,
  dateRange: CalendarDaysIcon,
  delivery: TruckIcon,
  desktop: MonitorIcon,
  documents: FolderClosedIcon,
  documentSigned: FileCheckIcon,
  email: MailIcon,
  event: SquareMousePointerIcon,
  eye: EyeIcon,
  eyeOff: EyeOffIcon,
  fileText: FileTextIcon,
  gift: GiftIcon,
  globe: GlobeIcon,
  grid: LayoutGridIcon,
  heart: HeartIcon,
  history: HistoryIcon,
  home: HouseIcon,
  id: HashIcon,
  image: ImageIcon,
  inbox: InboxIcon,
  key: KeyIcon,
  link: LinkIcon,
  list: ListIcon,
  mail: MailIcon,
  mailRead: MailOpenIcon,
  message: MessageSquareIcon,
  minus: MinusIcon,
  mobile: SmartphoneIcon,
  notes: SquarePenIcon,
  notificationAlert: BellRingIcon,
  notificationOff: BellOffIcon,
  number: HashIcon,
  objects: BoxIcon,
  overview: EyeIcon,
  pencil: PencilIcon,
  percentage: PercentIcon,
  phone: PhoneIcon,
  pieChart: PieChartIcon,
  privateFolder: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      {...props}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4.875 3.75C3.21815 3.75 1.875 5.09315 1.875 6.75V8.4302V17.25C1.875 18.9069 3.21815 20.25 4.875 20.25H18.75C20.4069 20.25 21.75 18.9069 21.75 17.25V9.88849C21.75 8.23163 20.4069 6.88849 18.75 6.88849H13.2761C12.2481 6.88849 11.2916 6.36207 10.7416 5.49352L10.5209 5.14496C9.97086 4.27642 9.0144 3.75 7.98635 3.75H4.875ZM9.875 11.6875V12.5625C9.64294 12.5625 9.42038 12.6547 9.25628 12.8188C9.09219 12.9829 9 13.2054 9 13.4375V15.625C9 15.8571 9.09219 16.0796 9.25628 16.2437C9.42038 16.4078 9.64294 16.5 9.875 16.5H14.25C14.4821 16.5 14.7046 16.4078 14.8687 16.2437C15.0328 16.0796 15.125 15.8571 15.125 15.625V13.4375C15.125 13.2054 15.0328 12.9829 14.8687 12.8188C14.7046 12.6547 14.4821 12.5625 14.25 12.5625V11.6875C14.25 11.1073 14.0195 10.5509 13.6093 10.1407C13.1991 9.73047 12.6427 9.5 12.0625 9.5C11.4823 9.5 10.9259 9.73047 10.5157 10.1407C10.1055 10.5509 9.875 11.1073 9.875 11.6875ZM13.375 12.5625V11.6875C13.375 11.3394 13.2367 11.0056 12.9906 10.7594C12.7444 10.5133 12.4106 10.375 12.0625 10.375C11.7144 10.375 11.3806 10.5133 11.1344 10.7594C10.8883 11.0056 10.75 11.3394 10.75 11.6875V12.5625H13.375Z'
        fill='currentColor'
      />
    </svg>
  ),
  qrCode: QrCodeIcon,
  receipt: ReceiptTextIcon,
  relation: BoxIcon,
  reportFile: FileChartColumnIcon,
  sales: BriefcaseIcon,
  settings: SettingsIcon,
  sliders: SlidersHorizontalIcon,
  stack: LayersIcon,
  string: StringIcon,
  support: LifeBuoyIcon,
  terminal: TerminalIcon,
  textInput: TextCursorInputIcon,
  time: ClockIcon,
  trash: Trash2Icon,
  trendDown: TrendingDownIcon,
  trendUp: TrendingUpIcon,
  user: UserIcon,
  users: UsersIcon,
  variable: VariableIcon,

  // Text editing
  alignLeft: AlignLeftIcon,
  alignRight: AlignRightIcon,
  bold: BoldIcon,
  italic: ItalicIcon,
  strikethrough: StrikethroughIcon,
  underline: UnderlineIcon,

  // Property attributes (see the attributeIcons maps in lib/listings and market-apartment-bulk)
  accessible: PersonStandingIcon,
  balcony: SunIcon,
  bathtub: BathIcon,
  bike: BikeIcon,
  busStation: BusIcon,
  cafe: CoffeeIcon,
  caged: FenceIcon,
  changingRoom: ShirtIcon,
  coldSupply: AirVentIcon,
  coldWater: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      width='36'
      height='36'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path
        d='M6.75065 22.5C5.71713 22.5012 4.70631 22.1969 3.84511 21.6255C2.98391 21.0541 2.31072 20.241 1.91009 19.2883C1.50945 18.3356 1.39923 17.2858 1.59325 16.2706C1.78728 15.2555 2.27691 14.3203 3.00065 13.5825V5.25C3.00065 4.25544 3.39574 3.30161 4.099 2.59835C4.80226 1.89509 5.75609 1.5 6.75065 1.5C7.74521 1.5 8.69904 1.89509 9.4023 2.59835C10.1056 3.30161 10.5007 4.25544 10.5007 5.25V13.5825C11.2244 14.3203 11.714 15.2555 11.9081 16.2706C12.1021 17.2858 11.9919 18.3356 11.5912 19.2883C11.1906 20.241 10.5174 21.0541 9.6562 21.6255C8.795 22.1969 7.78417 22.5012 6.75065 22.5ZM6.75065 3C6.1541 3.0006 5.58215 3.23784 5.16032 3.65967C4.73849 4.08149 4.50125 4.65345 4.50065 5.25V14.2373L4.25165 14.4615C3.68531 14.9677 3.28604 15.6339 3.10669 16.372C2.92734 17.1101 2.97636 17.8853 3.24727 18.5949C3.51818 19.3046 3.9982 19.9152 4.6238 20.346C5.24939 20.7768 5.99107 21.0075 6.75065 21.0075C7.51024 21.0075 8.25191 20.7768 8.87751 20.346C9.50311 19.9152 9.98313 19.3046 10.254 18.5949C10.5249 17.8853 10.574 17.1101 10.3946 16.372C10.2153 15.6339 9.81599 14.9677 9.24965 14.4615L9.00065 14.2373V5.25C9.00006 4.65345 8.76281 4.08149 8.34099 3.65967C7.91916 3.23784 7.34721 3.0006 6.75065 3Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M4.80828 16.1184C5.07684 15.6606 5.4986 15.3125 5.99901 15.1356H7.49902C7.99943 15.3125 8.42119 15.6606 8.68975 16.1184C8.95831 16.5762 9.05638 17.1142 8.96662 17.6374C8.87687 18.1605 8.60507 18.635 8.19927 18.9771C7.79347 19.3193 7.27977 19.5069 6.74901 19.5069C6.21824 19.5069 5.70456 19.3193 5.29876 18.9771C4.89296 18.635 4.62116 18.1605 4.53141 17.6374C4.44165 17.1142 4.53972 16.5762 4.80828 16.1184Z'
        fill='currentColor'
      />
      <path
        d='M14.0659 20.0651C14.1772 20.1769 14.3112 20.2394 14.468 20.2525C15.3042 20.305 16.1338 20.0753 16.9568 19.5634C17.7799 19.0514 18.3025 18.1981 18.5246 17.0036C18.5638 16.8203 18.5246 16.6596 18.407 16.5215C18.2894 16.3834 18.1392 16.3144 17.9563 16.3144C17.8121 16.3144 17.6845 16.3603 17.5738 16.4522C17.463 16.5441 17.3945 16.6623 17.3684 16.8067C17.2116 17.6867 16.8327 18.294 16.2318 18.6284C15.6308 18.9629 15.0625 19.1105 14.5268 19.0711C14.3439 19.0585 14.1937 19.1079 14.0761 19.2192C13.9585 19.3305 13.8997 19.478 13.8997 19.6618C13.8992 19.8188 13.9546 19.9532 14.0659 20.0651Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M21 16.064C21 20.2625 18.5012 22.5 14.2679 22.5C12.9432 22.5 11.7849 22.2842 10.8184 21.8591L12 21.1031C12.6762 21.3385 13.4407 21.4624 14.2806 21.4624C17.7441 21.4624 19.9245 19.3559 19.9245 16.0096C19.9245 12.4187 15.8785 7.57099 14.638 6.16879C14.5939 6.11897 14.5391 6.07897 14.4774 6.05155C14.4157 6.02413 14.3487 6.00993 14.2808 6.00993C14.2129 6.00993 14.1458 6.02413 14.0841 6.05155C14.0225 6.07897 13.9677 6.11897 13.9235 6.16879C13.5097 6.63645 12.7838 7.48739 12 8.54713V6.90121C12.5473 6.2379 13.0537 5.66301 13.4546 5.2078C13.6232 5.01636 13.7732 4.84609 13.8997 4.69931C13.9537 4.6368 14.0206 4.58662 14.096 4.55222C14.1714 4.51781 14.2534 4.5 14.3364 4.5C14.4193 4.5 14.5013 4.51781 14.5767 4.55222C14.6521 4.58662 14.719 4.6368 14.773 4.69931C14.8685 4.81017 14.9768 4.93429 15.0957 5.07069C16.8641 7.09899 21 11.8425 21 16.064Z'
        fill='currentColor'
      />
    </svg>
  ),
  commonRoom: SpeechIcon,
  conferenceRoom: PresentationIcon,
  dishwasher: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      width='36'
      height='38'
      viewBox='0 0 36 36'
      fill='currentColor'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <g clipPath='url(#clip0_2_2)'>
        <rect width='36' height='36' fill='transparent' />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M7.46684 3.28572H27.2343C28.6769 3.28572 29.8506 4.43926 29.8506 5.85714V28.7143C29.8506 30.1322 28.6769 31.2857 27.2343 31.2857H7.46684C6.02423 31.2857 4.85056 30.1322 4.85056 28.7143V5.85714C4.85056 4.10924 6.02423 3.28572 7.46684 3.28572ZM6.61364 20L6.5 23.5H28L28.0875 20H6.61364ZM10 15C8.11338 15 6.80108 16.2015 6.61364 18H12C12.0581 17.1701 12.0945 16.6814 12.5 16C11.8344 15.4115 10.9146 15 10 15ZM17.3506 15C15.464 15 13.9877 16.2015 13.8003 18H19.5C19.5581 17.1701 19.5613 16.6814 19.9668 16C19.3014 15.4115 18.2657 15 17.3506 15ZM24.4565 15C22.5699 15 21.0938 16.2015 20.9063 18H28.0875C27.9001 16.2015 26.3432 15 24.4565 15ZM24.4565 13.5C25.8629 13.5 27.1263 14.133 28.0875 15V9.91187H21.4811C21.1212 10.8619 20.213 11 19.076 11H15.5876C14.4506 11 13.341 10.8619 12.9811 9.91187H6.59474L6.61364 15C7.57486 14.133 8.59367 13.5 10 13.5C11.327 13.5 12.5243 14.159 13.5 15C14.4493 14.1851 15.9924 13.5 17.3506 13.5C18.6783 13.5 19.9308 14.159 20.9063 15C21.8556 14.1851 23.0983 13.5 24.4565 13.5ZM20.8874 8.15947H28.0875L28.1064 5.85714C28.1064 5.38452 27.7152 5 27.2343 5H7.46684C6.98597 5 6.59475 5.38452 6.59475 5.85714L6.59474 8.15947H13.7814C14.263 8.15947 14.7154 8.101 14.7154 8.5517C14.7154 9.0017 15.1066 9.3678 15.5875 9.3678H19.0758C19.5567 9.3678 19.9479 9.0017 19.9479 8.5517C19.9479 8.101 20.4058 8.15947 20.8874 8.15947ZM27.2343 29.5714C27.7152 29.5714 28.1064 29.1869 28.1064 28.7143L28.1064 25.5H6.61364L6.59475 28.7143C6.59475 29.1869 6.98597 29.5714 7.46684 29.5714H27.2343Z'
          fill='currentColor'
          stroke='currentColor'
        />
      </g>
      <defs>
        <clipPath id='clip0_2_2'>
          <rect width='36' height='36' fill='transparent' />
        </clipPath>
      </defs>
    </svg>
  ),
  displayWindow: StoreIcon,
  electricitySupply: PlugIcon,
  elevator: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      height='36px'
      width='36px'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path
        d='M12 8H17C17.5523 8 18 8.44772 18 9V19C18 19.5523 17.5523 20 17 20H12M12 8H7C6.44772 8 6 8.44772 6 9V19C6 19.5523 6.44772 20 7 20H12M12 8V20M7.5 4.5L9 3L10.5 4.5M13.5 3L15 4.5L16.5 3'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  ),
  engineHeater: HeaterIcon,
  evCharging: ZapIcon,
  fan: FanIcon,
  freightElevator: ForkliftIcon,
  garage: ParkingSquareIcon,
  garageType: WarehouseIcon,
  gym: DumbbellIcon,
  heatSupply: HeaterIcon,
  internet: EthernetPortIcon,
  investmentSupport: HousePlusIcon,
  kitchen: CookingPotIcon,
  laundry: WashingMachineIcon,
  loadingDock: TruckIcon,
  meetingRoom: UsersIcon,
  orientation: CompassIcon,
  outdoorOffice: SunIcon,
  parkingSpot: ParkingCircleIcon,
  privatePatio: TreePineIcon,
  reception: ConciergeBellIcon,
  restaurant: UtensilsIcon,
  roof: HouseIcon,
  sauna: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      fill='currentColor'
      height='36px'
      width='36px'
      version='1.1'
      id='Layer_1'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 512 512'
      {...props}
    >
      <g>
        <g>
          <g>
            <path
              d='M145.723,122.782c3.167,5.122,8.646,7.94,14.257,7.94c3.004,0,6.047-0.809,8.789-2.505
				c7.865-4.864,10.3-15.182,5.435-23.047c-3.651-5.904-1.881-14.19,1.468-27.134c3.599-13.908,8.077-31.218-1.306-47.048
				c-4.714-7.954-14.981-10.581-22.941-5.867c-7.954,4.715-10.581,14.986-5.866,22.941c2.282,3.85-0.051,12.866-2.306,21.586
				C139.26,85.084,134.29,104.294,145.723,122.782z'
            />
            <path
              d='M199.329,122.782c3.168,5.122,8.648,7.941,14.258,7.941c3.004,0,6.046-0.808,8.789-2.505
				c7.865-4.862,10.3-15.181,5.436-23.046c-3.651-5.905-1.882-14.19,1.467-27.135c3.599-13.909,8.076-31.217-1.305-47.047
				c-4.715-7.956-14.988-10.581-22.941-5.867c-7.956,4.715-10.582,14.986-5.868,22.94c2.283,3.85-0.05,12.867-2.306,21.586
				C192.868,85.084,187.898,104.294,199.329,122.782z'
            />
            <path
              d='M508.918,165.908c-5.345-7.546-15.795-9.328-23.342-3.984c-3.474,2.462-83.264,60.714-112.363,227.314h-43.576
				l7.047-46.256c18.465-0.403,33.365-15.537,33.365-34.096v-34.415c0-13.115-7.442-24.517-18.322-30.226l10.075-66.137
				c0.734-4.826-0.673-9.732-3.853-13.434c-3.181-3.701-7.818-5.832-12.7-5.832H24.799c-4.881,0-9.518,2.13-12.7,5.832
				c-3.181,3.703-4.588,8.609-3.853,13.434l10.076,66.138C7.442,249.955,0,261.358,0,274.471v34.415
				c0,18.558,14.9,33.692,33.364,34.096l12.371,81.199c0.724,35.986,30.224,65.037,66.381,65.037h5.841
				c0.008,0,0.016,0.001,0.023,0.001c0.009,0,0.018-0.001,0.026-0.001h134.038c0.009,0,0.018,0.001,0.026,0.001
				s0.016-0.001,0.023-0.001h5.84c8.99,0,17.568-1.799,25.395-5.051c5.757,0.945,11.647,1.441,17.632,1.441
				c46.692,0,87.742-29.746,102.285-74.063c0.372-1.058,0.641-2.163,0.796-3.306c4.016-26.436,9.387-49.981,15.489-70.819
				c13.464-45.978,30.494-78.768,44.41-100.293c2.72-4.207,5.397-8.108,8-11.714c18.284-25.33,32.895-36.089,32.993-36.163
				C512.479,183.905,514.263,173.454,508.918,165.908z M292.265,192.331h33.496l-7.316,48.023h-30.193L292.265,192.331z
				 M201.768,192.331h56.893l-4.013,48.023h-52.88V192.331z M168.28,192.331v48.023h-52.879l-4.013-48.023H168.28z M77.782,192.331
				l4.014,48.023H51.602l-7.316-48.023H77.782z M79.208,422.824c0-0.844-0.065-1.687-0.191-2.522l-11.776-77.3h23.135l9.214,110.246
				C87.64,448.311,79.208,436.535,79.208,422.824z M34.118,309.514c-0.348,0-0.631-0.281-0.631-0.627v-34.415
				c0-0.347,0.284-0.628,0.631-0.628h301.813c0.348,0,0.63,0.282,0.63,0.628v34.415c0,0.346-0.282,0.627-0.63,0.627H34.118z
				 M302.808,343.001l-7.044,46.237H275.81l3.864-46.237H302.808z M168.28,455.731h-34.878l-9.421-112.73h44.299V455.731z
				 M201.768,343.001h44.302l-3.864,46.237h-27.734c-4.914,0-9.537,2.176-12.703,5.877V343.001z M201.768,455.731v-35.927
				c5.801,13.798,14.273,25.941,24.687,35.927H201.768z M300.96,452.121c-23.792,0-45.457-11.207-59.181-29.395h118.36
				C346.417,440.914,324.753,452.121,300.96,452.121z'
            />
          </g>
        </g>
      </g>
    </svg>
  ),
  securityDoor: DoorClosedLockedIcon,
  sharedPatio: TreesIcon,
  shower: ShowerHeadIcon,
  signageLocation: SignpostIcon,
  toilet: ToiletIcon,
  trainStation: TrainFrontIcon,
  tumbler: DiscAlbumIcon,
  tvPackage: TvIcon,
  warmWater: (props: React.SVGProps<SVGSVGElement>) => (
    <svg
      width='36'
      height='36'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      {...props}
    >
      <path
        d='M6.75065 22.5C5.71713 22.5012 4.70631 22.1969 3.84511 21.6255C2.98391 21.0541 2.31072 20.241 1.91009 19.2883C1.50945 18.3356 1.39923 17.2858 1.59325 16.2706C1.78728 15.2555 2.27691 14.3203 3.00065 13.5825V5.25C3.00065 4.25544 3.39574 3.30161 4.099 2.59835C4.80226 1.89509 5.75609 1.5 6.75065 1.5C7.74521 1.5 8.69904 1.89509 9.4023 2.59835C10.1056 3.30161 10.5007 4.25544 10.5007 5.25V13.5825C11.2244 14.3203 11.714 15.2555 11.9081 16.2706C12.1021 17.2858 11.9919 18.3356 11.5912 19.2883C11.1906 20.241 10.5174 21.0541 9.6562 21.6255C8.795 22.1969 7.78417 22.5012 6.75065 22.5ZM6.75065 3C6.1541 3.0006 5.58215 3.23784 5.16032 3.65967C4.73849 4.08149 4.50125 4.65345 4.50065 5.25V14.2373L4.25165 14.4615C3.68531 14.9677 3.28604 15.6339 3.10669 16.372C2.92734 17.1101 2.97636 17.8853 3.24727 18.5949C3.51818 19.3046 3.9982 19.9152 4.6238 20.346C5.24939 20.7768 5.99107 21.0075 6.75065 21.0075C7.51024 21.0075 8.25191 20.7768 8.87751 20.346C9.50311 19.9152 9.98313 19.3046 10.254 18.5949C10.5249 17.8853 10.574 17.1101 10.3946 16.372C10.2153 15.6339 9.81599 14.9677 9.24965 14.4615L9.00065 14.2373V5.25C9.00006 4.65345 8.76281 4.08149 8.34099 3.65967C7.91916 3.23784 7.34721 3.0006 6.75065 3Z'
        fill='currentColor'
      />
      <path
        d='M7.49902 15.1356V5.24756H5.99901V15.1356C5.4986 15.3125 5.07684 15.6606 4.80828 16.1184C4.53972 16.5762 4.44165 17.1142 4.53141 17.6374C4.62116 18.1605 4.89296 18.635 5.29876 18.9771C5.70456 19.3193 6.21824 19.5069 6.74901 19.5069C7.27977 19.5069 7.79347 19.3193 8.19927 18.9771C8.60507 18.635 8.87687 18.1605 8.96662 17.6374C9.05638 17.1142 8.95831 16.5762 8.68975 16.1184C8.42119 15.6606 7.99943 15.3125 7.49902 15.1356Z'
        fill='currentColor'
      />
      <path
        d='M14.0659 20.0651C14.1772 20.1769 14.3112 20.2394 14.468 20.2525C15.3042 20.305 16.1338 20.0753 16.9568 19.5634C17.7799 19.0514 18.3025 18.1981 18.5246 17.0036C18.5638 16.8203 18.5246 16.6596 18.407 16.5215C18.2894 16.3834 18.1392 16.3144 17.9563 16.3144C17.8121 16.3144 17.6845 16.3603 17.5738 16.4522C17.463 16.5441 17.3945 16.6623 17.3684 16.8067C17.2116 17.6867 16.8327 18.294 16.2318 18.6284C15.6308 18.9629 15.0625 19.1105 14.5268 19.0711C14.3439 19.0585 14.1937 19.1079 14.0761 19.2192C13.9585 19.3305 13.8997 19.478 13.8997 19.6618C13.8992 19.8188 13.9546 19.9532 14.0659 20.0651Z'
        fill='currentColor'
      />
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M21 16.064C21 20.2625 18.5012 22.5 14.2679 22.5C12.9432 22.5 11.7849 22.2842 10.8184 21.8591L12 21.1031C12.6762 21.3385 13.4407 21.4624 14.2806 21.4624C17.7441 21.4624 19.9245 19.3559 19.9245 16.0096C19.9245 12.4187 15.8785 7.57099 14.638 6.16879C14.5939 6.11897 14.5391 6.07897 14.4774 6.05155C14.4157 6.02413 14.3487 6.00993 14.2808 6.00993C14.2129 6.00993 14.1458 6.02413 14.0841 6.05155C14.0225 6.07897 13.9677 6.11897 13.9235 6.16879C13.5097 6.63645 12.7838 7.48739 12 8.54713V6.90121C12.5473 6.2379 13.0537 5.66301 13.4546 5.2078C13.6232 5.01636 13.7732 4.84609 13.8997 4.69931C13.9537 4.6368 14.0206 4.58662 14.096 4.55222C14.1714 4.51781 14.2534 4.5 14.3364 4.5C14.4193 4.5 14.5013 4.51781 14.5767 4.55222C14.6521 4.58662 14.719 4.6368 14.773 4.69931C14.8685 4.81017 14.9768 4.93429 15.0957 5.07069C16.8641 7.09899 21 11.8425 21 16.064Z'
        fill='currentColor'
      />
    </svg>
  ),
  washingMachine: WashingMachineIcon,
  wasteDisposal: Trash2Icon,
};

export function getIcon(key: any) {
  if (typeof key !== 'string') {
    return undefined;
  }
  const icon = Icons[key as keyof typeof Icons];
  return icon as FC<React.SVGProps<SVGSVGElement>> | undefined;
}
